import type TauriDatabase from "@tauri-apps/plugin-sql";
import { type Database as BunDatabase } from "bun:sqlite";

import { type SqliteColumnType, type SqliteTableDef } from "../schema";
import { runGetTableXInfo } from "./runGetTableXInfo";
import { runMigration } from "./runMigration";

/**
 * Migrates a table by deleting a column. This creates a new table with the desired schema,
 * copies all data from the old table, drops the old table, and renames the new table.
 */
export async function runColumnDeleteMigration(
	db: BunDatabase | TauriDatabase,
	tableName: string,
	columnName: string
): Promise<void> {
	// Get current table info
	const currentTable = await runGetTableXInfo(db, tableName);

	const columnIndex = currentTable.columns.findIndex((col) => col.name === columnName);

	if (columnIndex === -1) {
		throw new Error(`Column ${columnName} not found in table ${tableName}`);
	} else if (currentTable.columns[columnIndex]?.pk === 1) {
		throw new Error(`Cannot delete primary key column ${columnName}`);
	}

	const columns = [...currentTable.columns];
	columns.splice(columnIndex, 1);

	// Create desired table definition without the specified column
	const desiredTable: SqliteTableDef = {
		tableName,
		columns: columns.map((col) => ({
			name: col.name,
			type: col.type as SqliteColumnType,
			nullable: col.notnull === 0,
			primaryKey: col.pk === 1,
			unique: col.pk === 1,
		})),
	};

	await runMigration(db, desiredTable);
}
